import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ELanguages } from '../../i18n';
import { allowedLanguages } from '../../i18n';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { selectUserId } from '../Login/LoginSlice';
import { refreshUser, refreshUserPermissions } from '../Login/LoginSlice/actionCreators';
import { fetchTotalUnread } from '../ChatsList/chatListSlice/actionCreators';
import { selectIsOnLine } from '../CheckAppAvailable/checkAppAvailableSlice';
import { setLanguage } from '../../components/SidebarOptions/Settings/settingsSlice/actionCreators';
import { getItem, sessionStorageGet, sessionStorageRemove, setItem } from '../../utils/storage';
import { isDesktop } from '../../utils/isDesktop';
import { mapFromPreferredLanguageToELanguages } from '../../apis/userAPI/helpers';
import { getUserSettings } from '../../api/users';
import { selectTenantId } from '../Organizations/OrganizationsSlice';
import { syncInactiveSessionAlarms } from '../Organizations/OrganizationsSlice/syncSessionIndicators';
import { debounce } from 'lodash';
import { TenantSwitchingOverlay } from '../../components/TenantSwitchingOverlay/TenantSwitchingOverlay';
import { Toaster } from 'react-hot-toast';

export function AuthApp({ children }: { children: React.ReactElement }) {
  const userId = useAppSelector(selectUserId);
  const isOnline = useAppSelector(selectIsOnLine);
  const tenantId = useAppSelector(selectTenantId);
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const prevIsOnlineRef = useRef<boolean>(isOnline);

  const getPreferredLanguage = async (currentUserId: number): Promise<ELanguages | null> => {
    if (!tenantId) {
      return null;
    }

    const { data, error } = await getUserSettings({ userId: currentUserId, tenantId });
    if (error) {
      return null;
    }

    return data.data.preferredLanguage
      ? mapFromPreferredLanguageToELanguages(data.data.preferredLanguage)
      : null;
  };

  const handleLanguage = async () => {
    if (!userId) {
      return;
    }

    const initialLang = sessionStorageGet('selectedLang') as ELanguages | null;
    if (initialLang) {
      setItem('language', initialLang);
      i18n.changeLanguage(initialLang);
      dispatch(setLanguage(userId, initialLang));
      sessionStorageRemove('selectedLang');

      return;
    }

    const preferredLanguage = await getPreferredLanguage(userId);
    if (
      getItem('language') !== preferredLanguage &&
      preferredLanguage !== null &&
      preferredLanguage !== undefined &&
      allowedLanguages.includes(preferredLanguage as ELanguages)
    ) {
      setItem('language', preferredLanguage);
      i18n.changeLanguage(preferredLanguage);
      return;
    }

    if (preferredLanguage === null) {
      dispatch(setLanguage(userId, i18n.language as ELanguages));
    }
  };

  useEffect(() => {
    if (!userId || !isOnline) {
      return;
    }

    handleLanguage();
    // Disabling the exhaustive deps hooks to not introduce any regressions during a structural refactoring
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOnline, userId, tenantId]);

  useEffect(() => {
    const wasOffline = prevIsOnlineRef.current === false;
    prevIsOnlineRef.current = isOnline;

    if (!userId || !(wasOffline && isOnline)) {
      return;
    }

    dispatch(refreshUser());
    dispatch(refreshUserPermissions());
  }, [dispatch, isOnline, userId]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined = undefined;
    const getUnread = () => {
      dispatch(fetchTotalUnread());
    };

    if (userId && isOnline) {
      if (!isDesktop()) {
        interval = setInterval(getUnread, 15000);
      } else {
        getUnread();
      }
    }
    if (!isOnline) {
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dispatch, userId, isOnline]);

  useEffect(() => {
    if (!isDesktop() || !userId) return;

    const debouncedFetch = debounce(() => {
      dispatch(fetchTotalUnread());
      dispatch(syncInactiveSessionAlarms());
    }, 1000);
    const subscription = window.electronApi?.onAppForeground(debouncedFetch);

    return () => {
      debouncedFetch.cancel();
      subscription?.remove();
    };
  }, [dispatch, userId]);

  // Browser equivalent of the Electron `on-app-foreground` hook — re-sync
  // inactive-session alarm flags when the tab becomes visible. Push remains
  // source of truth; this only reconciles drift while the tab was hidden.
  useEffect(() => {
    if (!userId || isDesktop()) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        dispatch(syncInactiveSessionAlarms());
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [dispatch, userId]);

  return (
    <>
      {children}
      <TenantSwitchingOverlay />
      <Toaster
        position="bottom-center"
        containerStyle={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)', zIndex: 999 }}
      />
    </>
  );
}
